import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle2, FileText, Paperclip, ShieldCheck, Upload, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { FeedPost, MyReachStatus, PostAttachment } from '@up2d8/contracts';
import {
  AppSubBar,
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  LoadingState,
  useToast,
} from '@up2d8/ui';
import { cachedPost, cachedStatus, removePost, savePost, saveStatuses } from '../../offline/db';
import { enqueue } from '../../offline/outbox';
import { HANDLES_OFFLINE, NotOnDeviceError, isNotOnDevice } from '../../offline/network-mode';
import { useOffline } from '../../offline/use-offline';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskPostDetail } from './desk/desk-post-detail';
import { DeskShell } from './desk/desk-shell';
import { PostTranslationNotice } from './post-translation-notice';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { useFileUrl } from '../../shared/use-upload';
import { CommentSection } from './app-comments';
import { PollBlock } from './app-poll';
import {
  AcknowledgeProgress,
  InteractionBar,
  SaveButton,
  useCommentCounts,
  useFeedCounts,
  useLikes,
} from './app-interaction';
import { formatDateTime } from '../../shared/format';
import { SafeHtml } from '../../shared/safe-html';
import { useLinkClickTracker } from '../../shared/link-clicks';

/**
 * Eén bericht, zoals een medewerker het leest.
 *
 * Eén kolom, grote tekst, en de bijlagen onderaan: afbeeldingen meteen zichtbaar, een pdf
 * achter een knop. Dat onderscheid is niet cosmetisch — een foto van een werkinstructie wil je
 * zien zonder eerst iets te downloaden, en een pdf van tien pagina's juist niet op je
 * databundel.
 */
export function AppPostPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { postId } = useParams({ strict: false }) as { postId: string };
  const trackLink = useLinkClickTracker(postId);
  const tabs = useAppTabs();

  /**
   * Toont de lezer de vertaling of het origineel?
   *
   * In state en niet in de URL: het is een keuze van dit moment ("laat me even zien wat er
   * werkelijk stond") en geen voorkeur. Wie altijd Nederlands wil, zet dat in zijn profiel.
   */
  const [showOriginal, setShowOriginal] = useState(false);
  // Een ander bericht opent weer in de vertaling; de keuze gold voor het vorige.
  useEffect(() => setShowOriginal(false), [postId]);

  const { pending, refreshPending, flush, online } = useOffline();

  /**
   * Het bericht, met de lokale kopie als vangnet (Stap 12).
   *
   * Wat hier bewaard wordt, is het bericht **zoals het op het scherm stond**: in de taal die de
   * medewerker leest, met de versie erbij. Dat is precies wat hij straks bevestigt — een kopie in een
   * andere taal of van een andere versie zou bewijs opleveren over een tekst die hij niet las.
   *
   * Het origineel wordt niet bewaard: dat is een handeling van het moment ("laat me even zien wat er
   * werkelijk stond") en niemand zoekt daarnaar in vliegtuigmodus.
   */
  const post = useQuery({
    queryKey: ['feed', postId, showOriginal ? 'original' : 'translated'],
    // Offline valt de functie zelf terug op de kopie; gepauzeerd bleef ze eindeloos laden (#15).
    ...HANDLES_OFFLINE,
    queryFn: async () => {
      let answered = false;
      try {
        const { data, error, response } = showOriginal
          ? await api.GET('/api/v1/feed/{postId}/original', { params: { path: { postId } } })
          : await api.GET('/api/v1/feed/{postId}', { params: { path: { postId } } });

        // De server heeft geantwoord, wat er ook in dat antwoord staat.
        answered = response !== undefined;

        if (error || !data) {
          // Een `404` is een oordeel: dit bericht is ingetrokken of was nooit voor deze medewerker.
          // Dan gaat de lokale kopie weg — een instructie die niet meer geldt, mag niet blijven staan
          // omdat hij toevallig op het toestel stond. Precies dat ging hier eerst mis.
          if (response?.status === 404) await removePost(postId);
          throw error ?? new Error('Ophalen mislukt');
        }

        const found = data as FeedPost;
        if (!showOriginal) await savePost(found);
        return found;
      } catch (error) {
        // Alleen zonder antwoord terugvallen op de kopie: dat is een telefoon zonder bereik.
        if (answered || showOriginal) throw error;
        const local = await cachedPost(postId);
        // Geen antwoord en geen kopie: nooit geopend met netwerk. Dat is iets anders dan "er niet".
        if (!local) throw new NotOnDeviceError();
        return local;
      }
    },
  });

  /**
   * Mijn eigen stand bij dit bericht: gezien, en of ik bevestigd heb.
   *
   * Hetzelfde antwoord dat mijn werkgever krijgt (acceptatiecriterium 6): als er iets over mij
   * geregistreerd wordt, hoor ik het te kunnen zien.
   */
  const status = useQuery({
    queryKey: ['my-reach', postId],
    ...HANDLES_OFFLINE,
    queryFn: async () => {
      try {
        const { data, error, response } = await api.GET('/api/v1/feed/{postId}/my-status', {
          params: { path: { postId } },
        });

        if (error || !data) {
          // Heeft de server geantwoord, dan geldt dat antwoord: geen stand betekent geen stand.
          if (response !== undefined) return null;
          return cachedStatus(postId);
        }

        const found = data as MyReachStatus;
        await saveStatuses([found]);
        return found;
      } catch {
        // Geen antwoord: de stand van de vorige keer. Zonder deze tak verdwijnt offline de knop
        // "Gelezen en begrepen", want dan weet het scherm niet dat dit bericht een bevestiging vraagt.
        return cachedStatus(postId);
      }
    },
  });

  /**
   * "Gezien" gaat één keer de deur uit, zodra het bericht op het scherm staat.
   *
   * In een `ref` en niet in state: bij elke rerender zou een nieuwe aanroep uitgaan, en dan is
   * "gezien op" geen tijdstip meer maar een teller.
   */
  const seenSent = useRef<string | null>(null);
  useEffect(() => {
    // Per bericht: via "vervangen door", een melding of de zoekfunctie blijft deze pagina staan
    // en komt er alleen een ander `postId` binnen.
    if (!post.data || seenSent.current === postId) return;
    seenSent.current = postId;

    const occurredAt = new Date().toISOString();
    void (async () => {
      try {
        const { error } = await api.POST('/api/v1/feed/{postId}/seen', {
          params: { path: { postId } },
          body: { clientOccurredAt: occurredAt } as never,
        });
        if (error) throw error;
        await queryClient.invalidateQueries({ queryKey: ['my-reach', postId] });
      } catch {
        // Offline: in de wachtrij met het tijdstip van nú. Zonder dat tijdstip zou in het rapport
        // staan dat hij het bericht las op het moment dat zijn telefoon weer bereik had (ADR-0047).
        await enqueue({ kind: 'seen', postId, clientOccurredAt: occurredAt });
      }
    })();
  }, [post.data, api, postId, queryClient]);

  const acknowledge = useMutation({
    // Offline niet laten pauzeren: zonder netwerk gaat de bevestiging in de wachtrij.
    ...HANDLES_OFFLINE,
    mutationFn: async (): Promise<'verzonden' | 'in-wachtrij'> => {
      const postVersion = post.data?.version ?? 1;
      const languageShown = post.data?.languageShown ?? post.data?.sourceLanguage ?? 'nl';
      const occurredAt = new Date().toISOString();

      try {
        const { error } = await api.POST('/api/v1/feed/{postId}/acknowledge', {
          params: { path: { postId } },
          body: {
            postVersion,
            // De taal die werkelijk op het scherm stond, en niet de brontaal: bewijs van "ik heb het
            // gelezen" hoort te zeggen wélke tekst hij las (ADR-0042).
            languageShown,
            clientOccurredAt: occurredAt,
          } as never,
        });
        if (error) throw error;
        return 'verzonden';
      } catch (error) {
        // Een antwoord van de server is een oordeel en gaat niet in de wachtrij: bij een `409` moet
        // hij de nieuwe tekst lezen, en bij een `422` is er iets met het tijdstip. Alleen een
        // verbinding die er niet is, is een reden om te wachten.
        const code = problemCode(error);
        if (code === 'CONFLICT' || code === 'CLIENT_TIME_REJECTED') throw error;

        await enqueue({
          kind: 'acknowledge',
          postId,
          postVersion,
          languageShown,
          clientOccurredAt: occurredAt,
        });
        return 'in-wachtrij';
      }
    },
    onSuccess: async (result) => {
      await refreshPending();
      await queryClient.invalidateQueries({ queryKey: ['my-reach', postId] });
      await queryClient.invalidateQueries({ queryKey: ['feed'] });
      // Ook de tellerrij: zonder deze regel blijft "18 van 32 bevestigd" op 18 staan nadat de
      // medewerker zelf net bevestigde, en dan lijkt zijn tik niet aangekomen. Gevonden door de
      // e2e-test die het getal vóór en ná de bevestiging vergelijkt.
      await queryClient.invalidateQueries({ queryKey: ['feed-counts'] });
      // "Voor jou" en Mijn taken: anders staat het net bevestigde bericht daar nog.
      await queryClient.invalidateQueries({ queryKey: ['open-items'] });

      toast.show({
        title: result === 'verzonden' ? t('acknowledge.done') : t('offline.acknowledgeQueued'),
        tone: 'success',
      });
      if (result === 'in-wachtrij' && online) void flush();
    },
    onError: (error) =>
      toast.show({
        title:
          problemCode(error) === 'CONFLICT' ? t('acknowledge.changed') : t('acknowledge.failed'),
        tone: 'danger',
      }),
  });

  /**
   * De tellers van dit ene bericht (Stap 12b).
   *
   * Dezelfde hooks als de feed, met een lijst van één. Dat lijkt omslachtig maar houdt één
   * waarheid: zou het detail zijn eigen route hebben, dan kan het getal op de kaart en het getal
   * op het detail uiteenlopen — en dan gaat iemand dat verschil verklaren.
   */
  const ids = [postId];
  const likes = useLikes(ids);
  const counts = useFeedCounts(ids);
  const commentCounts = useCommentCounts(ids);

  const like = likes.data?.[0];
  const postCounts = counts.data?.[0];

  const acknowledged = status.data?.acknowledgedAt ?? null;
  const inQueue = pending.has(postId);
  // Voor de medewerker is een bevestiging in de wachtrij gedaan: hij drukte op de knop. De knop
  // opnieuw aanbieden zou vragen om iets wat hij al deed.
  const needsAcknowledgement =
    (status.data?.requiresAcknowledgement ?? false) && !acknowledged && !inQueue;

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="home"
      renderLink={renderAppTabLink}
    >
      {/* --- Bureauscherm: de pagina uit de replica-spec --- */}
      <div className="hidden desk:block">
        {post.isPending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : post.data ? (
          <DeskPostDetail
            postId={postId}
            post={post.data}
            needsAcknowledgement={needsAcknowledgement}
            acknowledgedAt={acknowledged}
            acknowledgedVersion={status.data?.acknowledgedVersion ?? null}
            acknowledging={acknowledge.isPending}
            onAcknowledge={() => acknowledge.mutate()}
            onBack={() => void navigate({ to: '/app' })}
            translationNotice={
              <PostTranslationNotice
                post={post.data}
                showOriginal={showOriginal}
                onShowOriginal={setShowOriginal}
              />
            }
          />
        ) : (
          <PostMissing error={post.error} />
        )}
      </div>

      {/* --- Telefoon: de pagina zoals ze was --- */}
      <div className="flex flex-col gap-element desk:hidden">
        {/*
          De kopbalk met de terugknop hoort bij de **telefoon**, niet bij het bureauscherm: daar
          staat de navigatie links en is terug een stap in de geschiedenis van de browser.

          Ze staat hier en niet in `DeskShell`, omdat niet elk scherm er een heeft - de tijdlijn en
          de agenda zijn bestemmingen van de onderbalk en hebben nergens om naar terug te gaan.
          Ze geeft ook de `h1` van het scherm; zonder haar had deze pagina er geen meer.
      */}
        <AppSubBar
          title={post.data?.title ?? t('appHome.title')}
          onBack={() => void navigate({ to: '/app' })}
        />
        {post.isPending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : post.data ? (
          <Card>
            {/*
            De titel staat ook al in de kopbalk (AppSubBar geeft die door als h1, ingekort
            als hij niet past). Hier volledig en als h2: één h1 per scherm, en de lezer ziet
            de hele titel zodra hij scrolt voorbij een kopbalk die hem had afgekapt.
          */}
            <h2 className="font-heading text-title-lg font-semibold text-text-primary">
              {post.data.title}
            </h2>
            <p className="mt-1 text-small text-text-muted">
              {formatDateTime(post.data.publishedAt)}
            </p>
            {post.data.requiresAcknowledgement && (
              <Badge tone="warning" className="mt-2">
                {t('feed.ackNeeded')}
              </Badge>
            )}

            {/*
              Dit bericht is ingetrokken en vervangen (Extra 3, ADR-0070).

              Een bericht met leesbevestiging ligt na publicatie vast, dus een verbetering is een
              nieuw bericht. Zonder deze regel leest het oude als "dit klopt niet meer" en moet de
              lezer zelf uitzoeken wat er dan wel klopt - terwijl er in zijn tijdlijn een bijna
              identiek bericht staat.
            */}
            {post.data.replacedBy && (
              <Callout
                tone="warning"
                className="mt-element"
                data-testid="post-replaced-by"
                title={t('posts.replacedByTitle')}
              >
                <Link
                  to="/app/posts/$postId"
                  params={{ postId: post.data.replacedBy.postId }}
                  className="font-medium text-brand-primary underline"
                >
                  {post.data.replacedBy.title}
                </Link>
              </Callout>
            )}

            <div className="mt-element empty:hidden">
              <PostTranslationNotice
                post={post.data}
                showOriginal={showOriginal}
                onShowOriginal={setShowOriginal}
              />
            </div>

            {/*
              De HTML komt van de server en is daar met een witte lijst opgebouwd uit de
              editor-JSON (ADR-0037): er kan geen `<script>` in zitten, omdat er geen regel is
              die er een kan schrijven. `SafeHtml` schoont ze in de browser nog eens op.
            */}
            <SafeHtml
              className="prose-up2d8 mt-element text-content text-text-primary"
              html={post.data.bodyHtml}
              onClick={trackLink}
            />

            {post.data.requiresAcknowledgement && (
              // Transparantie in de app (ADR-0014): wie iets over je registreert, hoort te zeggen
              // dát hij dat doet — en waar je kunt lezen wat er precies bijgehouden wordt.
              <p className="mt-element flex items-start gap-2 rounded-lg bg-brand-primary-tint px-3 py-2 text-small text-text-secondary">
                <ShieldCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" />
                <span>
                  {t('acknowledge.transparency')}{' '}
                  <Link to="/app/privacy/werkgever" className="text-brand-primary underline">
                    {t('acknowledge.transparencyLink')}
                  </Link>
                </span>
              </p>
            )}

            {/*
              Zijn bevestiging staat in de wachtrij. Dit hoort op het scherm te blijven staan: de toast
              is na een paar seconden weg, de knop is verdwenen, en dan heeft de medewerker niets meer
              dat zegt dat zijn tik geregistreerd is. Bij een bevestiging die bewijs is, is dat het
              verschil tussen vertrouwen en nog een keer drukken.
            */}
            {inQueue && !acknowledged && (
              <p className="mt-element flex items-start gap-2 rounded-lg bg-brand-primary-tint px-3 py-2 text-content text-text-primary">
                <Upload aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary" />
                <span>
                  <span className="font-semibold">{t('offline.waitingToSend')}</span>{' '}
                  {t('offline.acknowledgeQueued')}
                </span>
              </p>
            )}

            {acknowledged && (
              <p className="mt-element flex items-center gap-2 rounded-lg bg-success-tint px-3 py-2 text-content text-text-primary">
                <CheckCircle2 aria-hidden className="h-5 w-5 text-success" />
                {t('acknowledge.confirmedAt', {
                  when: formatDateTime(acknowledged),
                  version: status.data?.acknowledgedVersion ?? 1,
                })}
              </p>
            )}

            {post.data.attachments.length > 0 && (
              <div className="mt-section flex flex-col gap-element border-t border-border-light pt-element">
                <h2 className="font-heading text-title-sm font-semibold text-text-primary">
                  {t('feed.attachments')}
                </h2>
                {post.data.attachments.map((file) => (
                  <Attachment key={file.fileId} file={file} />
                ))}
              </div>
            )}

            {/*
              De tellerrij staat ónder de tekst en de bevestiging, niet erboven: wat anderen deden,
              hoort niet te concurreren met wat jij moet lezen en bevestigen.
            */}
            <div className="mt-section flex flex-wrap items-center justify-between gap-element border-t border-border-light pt-element">
              <div className="flex flex-wrap items-center gap-1">
                <InteractionBar
                  postId={postId}
                  like={like}
                  viewCount={postCounts?.viewCount ?? null}
                  commentCount={commentCounts.data?.[0]?.count ?? null}
                  suppressed={postCounts?.suppressed ?? false}
                />
                {/* Opslaan, zoals op het bureauscherm: de lege lijst Opgeslagen verwijst hiernaar. */}
                <SaveButton postId={postId} saved={post.data.savedByMe} />
              </div>
              <AcknowledgeProgress
                acknowledgedCount={postCounts?.acknowledgedCount ?? null}
                recipientCount={postCounts?.recipientCount ?? null}
                suppressed={postCounts?.suppressed ?? false}
              />
            </div>
          </Card>
        ) : (
          // Een bericht dat niet voor jou was, bestaat voor jou niet. Geen "geen toegang":
          // dat zou zeggen dat er iets is dat je niet mag zien.
          <PostMissing error={post.error} />
        )}

        {/*
          De knop plakt onderaan het scherm, en dat is geen stijlkeuze: op een telefoon staat een
          bericht van vier alinea's met de knop onder de plooi, en dan bevestigt niemand. Wel ná de
          tekst in de voorleesorde, zodat een schermlezer eerst het bericht geeft.
        */}
        {needsAcknowledgement && (
          <div className="sticky bottom-0 -mx-4 mt-element border-t border-border bg-surface px-4 py-3">
            <Button
              className="w-full"
              loading={acknowledge.isPending}
              onClick={() => acknowledge.mutate()}
            >
              <CheckCircle2 aria-hidden className="h-5 w-5" />
              {t('acknowledge.button')}
            </Button>
            <p className="mt-2 text-center text-small text-text-muted">{t('acknowledge.final')}</p>
          </div>
        )}
      </div>

      {/*
        De peiling en de opmerkingen staan **één keer** in de DOM, onder beide indelingen.

        Niet in allebei de blokken: dan staan er twee tekstvakken met hetzelfde label "Jouw reactie"
        en twee knoppen "Plaatsen" op dezelfde pagina. Eén ervan is met CSS verborgen, maar een
        schermlezer en een test zien ze allebei - en een formulier dat tweemaal bestaat, is een
        formulier dat vroeg of laat tweemaal verstuurt.

        Dat de opmerkingen daardoor in een eigen kaart onder het bericht staan in plaats van in
        dezelfde kaart (bericht-detail-spec §4.7), staat als afwijking in `docs/design/deviations.md`.

        De peiling blijft boven de reacties: ze is een vraag van de opsteller en een reactie is een
        vraag van een collega. Wie het bericht opent omdat er gestemd moet worden, hoort niet eerst
        door een discussie te scrollen.
      */}
      {post.data && (
        <section
          // Het anker waar "Bekijk alle reacties" op de kaart naartoe springt (Extra 5). Een
          // `id` en geen scroll-in-code: de browser doet dit zelf, ook bij terugnavigeren.
          id="reacties"
          data-testid="post-discussion"
          className="mt-element scroll-mt-16 rounded-card border border-border-light bg-surface p-4 desk:mx-auto desk:w-full desk:max-w-[820px] desk:px-5 desk:py-5"
        >
          <PollBlock postId={postId} />
          <CommentSection postId={postId} />
        </section>
      )}
    </DeskShell>
  );
}

function Attachment({ file }: { file: PostAttachment }) {
  const { t } = useTranslation();
  const fileUrl = useFileUrl();
  const [url, setUrl] = useState<string | null>(null);

  // Alleen afbeeldingen worden meteen opgehaald. Een pdf wacht op een tik: op een telefoon in
  // de fabriek is databundel een echte kostenpost.
  useEffect(() => {
    if (!file.isImage) return;
    let current = true;
    void fileUrl(file.fileId, file.hasThumbnail ? 'thumbnail' : 'original').then((value) => {
      if (current) setUrl(value);
    });
    return () => {
      current = false;
    };
  }, [file, fileUrl]);

  if (file.isImage) {
    return url ? (
      <img src={url} alt={file.fileName} className="w-full rounded-lg" />
    ) : (
      <div className="h-32 w-full animate-pulse rounded-lg bg-background-warm" aria-hidden />
    );
  }

  return (
    <Button
      variant="secondary"
      className="justify-start"
      onClick={async () => {
        const link = await fileUrl(file.fileId);
        if (link) window.open(link, '_blank', 'noopener,noreferrer');
      }}
    >
      {file.mimeType === 'application/pdf' ? (
        <FileText aria-hidden className="h-4 w-4" />
      ) : (
        <Paperclip aria-hidden className="h-4 w-4" />
      )}
      {file.fileName}
      <span className="text-small text-text-muted">
        {Math.max(1, Math.round(file.sizeBytes / 1024))} kB
      </span>
      <span className="sr-only">{t('feed.openAttachment')}</span>
    </Button>
  );
}

/**
 * Geen bericht om te tonen. Offline en nooit geopend zegt iets anders dan ingetrokken: anders zoekt
 * de medewerker een bericht dat er gewoon is (testrapport #15).
 */
function PostMissing({ error }: { error: unknown }) {
  const { t } = useTranslation();
  return isNotOnDevice(error) ? (
    <EmptyState
      icon={<WifiOff aria-hidden className="h-8 w-8" />}
      title={t('offline.postUnavailableTitle')}
      description={t('offline.postUnavailableHint')}
    />
  ) : (
    <EmptyState title={t('feed.notFoundTitle')} description={t('feed.notFoundHint')} />
  );
}
